import { findFirstNonEmptyString, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useSeriesWidgetContext } from "~/components/article/article-left-hand-side-widgets/series-widget/contexts";
import { tracking } from "~/data";
import { useImageUtils } from "~/lib/hooks";
import type { seriesWidgetItemContent$key } from "~/queries/__generated__/seriesWidgetItemContent.graphql";

import {
  Container,
  Headline,
  Indicator,
  ItemContainer,
  StyledBaseImage,
  StyledEntityLink,
} from "./styles";

export type Props = {
  className?: string;
  index: number;
  isActive?: boolean;
  onClick: () => void;
  reference: seriesWidgetItemContent$key;
};

export const SeriesWidgetItem: FunctionComponent<Props> = ({
  index,
  isActive = false,
  onClick,
  reference: reference_,
}) => {
  const { getResizedImageUrl } = useImageUtils();

  const content = useFragment(
    graphql`
      fragment seriesWidgetItemContent on Article {
        ...entityLink
        headline
        socialHeadline
        images {
          title
          url
          isSlideshow
          style(filter: { style: "237x147" }) {
            url
          }
        }
      }
    `,
    reference_,
  );
  const { campaign } = useSeriesWidgetContext();

  const renderHeadline = () => findFirstNonEmptyString(content.socialHeadline, content.headline);

  const renderImage = () => {
    const images = content.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images.find(img => !!img?.isSlideshow) ?? first(images);
    const source = getResizedImageUrl(mainImage?.style?.url ?? mainImage?.url ?? "", 76);
    const alt = mainImage?.title ?? "";
    return (
      <StyledBaseImage
        $aspectRatio="237 / 147"
        $height="auto"
        $objectFit="cover"
        $width="76px"
        alt={alt}
        src={source}
        disableLinkingData
      />
    );
  };

  return content ? (
    <Container $isActive={isActive}>
      <StyledEntityLink
        query={{
          campaign,
          module: tracking.module.SeriesLHS,
          pgtype: tracking.pageType.Article,
        }}
        reference={content}
        onClick={onClick}
      >
        <ItemContainer>
          <Indicator>{index}</Indicator>
          <Headline>{renderHeadline()}</Headline>
          {renderImage()}
        </ItemContainer>
      </StyledEntityLink>
    </Container>
  ) : null;
};

SeriesWidgetItem.displayName = "SeriesWidgetItem";
